import { Component, inject, OnInit, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { AsyncPipe } from '@angular/common';
import { LocalizationModule } from '@abp/ng.core';
import { ToasterService } from '@abp/ng.theme.shared';
import { NgbActiveOffcanvas } from '@ng-bootstrap/ng-bootstrap';
import {
  NgSelectComponent,
  NgLabelTemplateDirective,
  NgOptionTemplateDirective,
} from '@ng-select/ng-select';
import {
  DwApiSelectDirective,
  DwDatePickerComponent,
  DwInputRequiredDirective,
  LoadingService,
} from '@digiwall/core';
import { DialogBoxComponent } from '@digiwall/ui-kit';
import {
  CreateProductionDocFromCustomerOrderDto,
  ProductionFolderDocService,
  WorkOrderDayBookDto,
  WorkOrderDayBookService,
} from '@digiwall/goliath-documents/proxy';
import { SupplierService, WorkerService } from '@digiwall/goliath-contacts/proxy';
import {
  CompanySiteDto,
  CompanySiteRole,
  CompanySiteService,
  DocRole,
  DwActiveLookupRequestDto,
} from '@digiwall/goliath-core/proxy';
import { Observable } from 'rxjs';
import {
  ProductionDocCreationGridComponent,
  ProductionDocCreationLine,
} from './production-doc-creation-grid/production-doc-creation-grid.component';

@Component({
  selector: 'document-production-doc-creation-dialog',
  templateUrl: './production-doc-creation-dialog.component.html',
  imports: [
    ReactiveFormsModule,
    AsyncPipe,
    LocalizationModule,
    DialogBoxComponent,
    NgSelectComponent,
    NgLabelTemplateDirective,
    NgOptionTemplateDirective,
    DwApiSelectDirective,
    DwDatePickerComponent,
    DwInputRequiredDirective,
    ProductionDocCreationGridComponent,
  ],
})
export class ProductionDocCreationDialogComponent implements OnInit {
  // Set imperatively by the opener (offcanvas) before change detection runs.
  public salesPurchaseId!: string;

  private readonly productionFolderDocService = inject(ProductionFolderDocService);
  private readonly workOrderDayBookService = inject(WorkOrderDayBookService);
  private readonly supplierService = inject(SupplierService);
  private readonly companySiteService = inject(CompanySiteService);
  private readonly workerService = inject(WorkerService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly toasterService = inject(ToasterService);
  private readonly activeCanvas = inject(NgbActiveOffcanvas);
  private readonly loadingService = inject(LoadingService);

  protected readonly workOrderDayBooks$: Observable<WorkOrderDayBookDto[]> =
    this.workOrderDayBookService.getAllByRole(DocRole.ProductionFolder);
  protected readonly productionSites$: Observable<CompanySiteDto[]> =
    this.companySiteService.getCompanySiteByRole(CompanySiteRole.Production);

  protected form: FormGroup;

  protected readonly minPickerDate = {
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    day: new Date().getDate(),
  };

  // The creation lines are edited/selected in the child grid, which mutates these objects in place.
  protected lines: ProductionDocCreationLine[] = [];
  protected readonly linesCanSave = signal(false);
  protected readonly isSaving = signal(false);

  ngOnInit(): void {
    this.initForm();

    this.productionFolderDocService
      .getDefaultsFromCustomerOrder(this.salesPurchaseId)
      .subscribeOnce(defaults => {
        this.lines = (defaults.lines ?? []) as ProductionDocCreationLine[];
        this.form.patchValue({
          productionSiteId: defaults.productionSiteId,
          workOrderDayBookId: defaults.workOrderDayBookId,
          responsibleId: defaults.responsibleId,
          manufacturerId: defaults.manufacturerId,
          preferredProductionDate: defaults.preferredProductionDate,
        });
      });
  }

  private initForm(): void {
    this.form = this.formBuilder.group({
      productionSiteId: [null, Validators.required],
      workOrderDayBookId: [null, Validators.required],
      responsibleId: [null, Validators.required],
      manufacturerId: [null, Validators.required],
      preferredProductionDate: [null, Validators.required],
      splitByPiece: [{ value: false, disabled: true }],
    });
  }

  protected save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    // At least one line selected, and every selected line fully set (also enforced by the backend).
    if (!this.linesCanSave()) {
      return;
    }

    const dto: CreateProductionDocFromCustomerOrderDto = {
      ...this.form.getRawValue(),
      lines: this.lines.filter(line => line.selected),
    };

    this.isSaving.set(true);
    this.loadingService.interceptNextRequest();

    this.productionFolderDocService
      .createFromCustomerSalesPurchase(this.salesPurchaseId, dto)
      .subscribeOnce(
        result => {
          if (result) {
            this.toasterService.success('::SavedSuccessfully');
            this.activeCanvas.close(result);
          } else {
            this.isSaving.set(false);
          }
        },
        () => this.isSaving.set(false),
      );
  }

  protected readonly manufacturerSelect = (dto: DwActiveLookupRequestDto<string>) => {
    dto.onlyActive = true;
    dto.sorting = [{ property: 'name' }];
    dto.queryProperties = ['code', 'name'];
    return this.supplierService.getLookupList(dto);
  };

  protected readonly workerSelect = (dto: DwActiveLookupRequestDto<string>) => {
    dto.onlyActive = true;
    dto.sorting = [{ property: 'lastName' }];
    dto.queryProperties = ['code', 'lastName', 'firstName'];
    return this.workerService.getLookupList(dto);
  };
}
